import { useState } from 'react';
import { Link } from 'react-router-dom';
import { get, post } from '../api';
import { askConfirm } from '../components/ask';
import { Problems } from '../components/Field';
import { FilterChips } from '../components/FilterChips';
import { currentLocale } from '../dates';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { formatMoney } from '../money';
import { BILL_PILL, BILL_STATUS, billPaid, payBill, unpayBill } from './billShared';
import { useMoneyFilter } from './moneyFilter';

interface Bill {
  id: number;
  contractor_name: string;
  invoice_number: string;
  invoice_date: string | null;
  total_ex_gst: string;
  gst_amount: string;
  expected_ex_gst: string | null;
  status: string;
  paid_at: string | null;
}

// In the order bills move: checked, sorted out if they don't match, paid.
const CHIP: [string, string][] = [
  ['received', BILL_STATUS.received!],
  ['disputed', BILL_STATUS.disputed!],
  ['matched', BILL_STATUS.matched!],
  ['paid', BILL_STATUS.paid!],
  ['', 'All'],
];
/** Steps a bill only sometimes passes through: shown when something is in them. */
const RARE = ['received', 'disputed'];
/** What each filter holds, said when it holds nothing. */
const EMPTY: Record<string, string> = {
  matched: 'No bills ready to pay. A bill is ready once its total matches what its visits pay.',
  received: 'No bills waiting to be checked.',
  disputed: 'Every bill matches what its visits pay.',
  paid: 'No paid bills yet.',
  '': 'No bills yet. The bill run proposes each contractor’s bill from the visits they led.',
};

const when = (iso: string) =>
  new Date(iso).toLocaleDateString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
const money = formatMoney;
/** A bill's date is a plain calendar date, so it is shown without shifting time zones. */
const billDate = (d: string | null) => (d ? when(`${d}T12:00:00`) : '');
/** How far the bill is from what its visits pay: blank when they agree. */
function difference(b: Bill): string {
  if (b.expected_ex_gst === null) return '';
  const diff =
    Math.round(Number(b.total_ex_gst) * 100) - Math.round(Number(b.expected_ex_gst) * 100);
  return diff === 0 ? '' : `${money(Math.abs(diff) / 100)} ${diff > 0 ? 'over' : 'under'}`;
}

/**
 * The bills for the visits contractors led: money going out. They are made in
 * the bill run, checked against what their visits pay, and marked paid here
 * once the money has gone, one at a time or several together.
 */
export function BillsTab() {
  const [tick, setTick] = useState(0);
  const [problems, setProblems] = useState<string[]>([]);
  const [paid, setPaid] = useState<{ ids: number[]; message: string } | null>(null);
  const [ticked, setTicked] = useState<number[]>([]);
  const [paidOn, setPaidOn] = useState(() => new Date().toLocaleDateString('en-CA'));
  const bills = useLoad(() => get<{ items: Bill[] }>('/api/bills?limit=200'), [tick]);

  async function act(run: () => Promise<unknown>) {
    setProblems([]);
    try {
      await run();
      setTick((t) => t + 1);
    } catch (e) {
      setProblems(describe(e).problems);
    }
  }
  const pay = (b: Bill) =>
    act(async () => {
      const message = await payBill(b);
      if (message) setPaid({ ids: [b.id], message });
    });
  const undo = (ids: number[]) =>
    act(async () => {
      for (const id of ids) await unpayBill(id);
      setPaid(null);
    });
  // Several ready bills paid in one go, e.g. after a bank batch, on the day the money went.
  const payTicked = (bills: Bill[]) =>
    act(async () => {
      const amount = formatMoney(
        bills.reduce((n, b) => n + Math.round(billPaid(b) * 100), 0) / 100,
      );
      const yes = await askConfirm({
        title: `Mark ${bills.length} bills paid?`,
        body: `Record that ${bills.map((b) => `${b.contractor_name} (${b.invoice_number})`).join(', ')} were paid ${amount} in all, including GST, on ${billDate(paidOn)}.`,
        confirmLabel: `Mark ${bills.length} paid`,
      });
      if (!yes) return;
      const r = await post<{ paid: number[] }>('/api/bills/paid', {
        ids: bills.map((b) => b.id),
        paid_on: paidOn,
      });
      setTicked([]);
      setPaid({ ids: r.paid, message: `${r.paid.length} bills marked paid: ${amount}.` });
    });

  const items = bills.data?.items ?? [];
  const counts = Object.fromEntries(
    CHIP.map(([k]) => [k, k ? items.filter((b) => b.status === k).length : items.length]),
  );
  // With no filter chosen, open on the first kind of bill that needs something done.
  const first = ['disputed', 'matched', 'received'].find((k) => counts[k]! > 0) ?? '';
  const [chip, setChip] = useMoneyFilter('bills', first);
  const shown = chip ? items.filter((b) => b.status === chip) : items;
  // The same figure Mark paid confirms: ready bills only, with their GST.
  const ready = items.filter((b) => b.status === 'matched');
  const readyTotal = ready.reduce((n, b) => n + Math.round(billPaid(b) * 100), 0) / 100;
  // Only ready bills can be paid together; one that doesn't match is sorted out on its own page.
  const batch = shown.filter((b) => b.status === 'matched');
  const chosen = batch.filter((b) => ticked.includes(b.id));

  return (
    <div className="money-tab">
      <div className="list-bar">
        <FilterChips
          label="Show"
          current={chip}
          chips={CHIP.filter(
            ([key]) => !RARE.includes(key) || counts[key]! > 0 || chip === key,
          ).map(([key, label]) => ({
            key,
            label,
            count: counts[key],
            alert: key === 'disputed',
          }))}
          onPick={setChip}
        />
        <div className="bar-action">
          {ready.length > 0 && (
            <span className="muted">
              Ready to pay: {money(readyTotal)} including GST across {ready.length}{' '}
              {ready.length === 1 ? 'bill' : 'bills'}
            </span>
          )}
          <Link className="button" to="/money/bill-run">
            Start the bill run
          </Link>
        </div>
      </div>
      {chosen.length > 0 && (
        <div className="row start bill-batch">
          <label>
            Paid on <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          </label>
          <button type="button" onClick={() => void payTicked(chosen)}>
            Mark {chosen.length} {chosen.length === 1 ? 'bill' : 'bills'} paid
          </button>
        </div>
      )}
      <Problems problems={problems} title="Could not do that." />
      {paid && (
        <p className="notice row start" role="status">
          <span>{paid.message}</span>
          <button type="button" className="quiet" onClick={() => void undo(paid.ids)}>
            Undo
          </button>
        </p>
      )}
      {bills.error && (
        <p role="alert" className="error">
          {bills.error}
        </p>
      )}
      {bills.loading && !bills.data && <p className="muted">Loading…</p>}
      {!bills.loading && !bills.error && shown.length === 0 && (
        <p className="empty">{EMPTY[chip] ?? EMPTY['']}</p>
      )}
      {shown.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                {batch.length > 1 && (
                  <th className="pick">
                    <input
                      type="checkbox"
                      aria-label={`Tick all ${batch.length} bills ready to pay`}
                      checked={chosen.length === batch.length}
                      onChange={() =>
                        setTicked(chosen.length === batch.length ? [] : batch.map((b) => b.id))
                      }
                    />
                  </th>
                )}
                <th>Contractor</th>
                <th>Bill</th>
                <th>Date</th>
                <th className="num">Total ex GST</th>
                <th className="num">Difference</th>
                <th>Status</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((b) => (
                <tr key={b.id}>
                  {batch.length > 1 && (
                    <td className="pick">
                      {b.status === 'matched' && (
                        <input
                          type="checkbox"
                          aria-label={`Tick ${b.invoice_number} from ${b.contractor_name}`}
                          checked={ticked.includes(b.id)}
                          onChange={() =>
                            setTicked((t) =>
                              t.includes(b.id) ? t.filter((x) => x !== b.id) : [...t, b.id],
                            )
                          }
                        />
                      )}
                    </td>
                  )}
                  <td>{b.contractor_name}</td>
                  <td>
                    <Link to={`/bills/${b.id}`}>{b.invoice_number}</Link>
                  </td>
                  <td>{billDate(b.invoice_date)}</td>
                  <td className="num">{money(b.total_ex_gst)}</td>
                  <td className="num">{difference(b)}</td>
                  <td className="status">
                    <span className={BILL_PILL[b.status] ?? 'pill'}>
                      {BILL_STATUS[b.status] ?? b.status}
                    </span>
                  </td>
                  <td className="actions">
                    {/* A disputed bill is sorted out on its own page, where the difference is shown. */}
                    {b.status === 'disputed' ? (
                      <Link className="button quiet-button" to={`/bills/${b.id}`}>
                        See the difference<span className="sr-only"> on {b.invoice_number}</span>
                      </Link>
                    ) : b.status === 'received' ? (
                      // Sent in from the portal: checked on its own page before it can be paid.
                      <Link className="button quiet-button" to={`/bills/${b.id}`}>
                        Check it<span className="sr-only"> {b.invoice_number}</span>
                      </Link>
                    ) : (
                      b.status !== 'paid' && (
                        <button type="button" className="quiet" onClick={() => void pay(b)}>
                          Mark paid<span className="sr-only"> {b.invoice_number}</span>
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
